import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { environment } from 'src/environments/environment';
import { UserService } from '../services/user/user.service';
import { lastValueFrom } from 'rxjs';
import { UserLoginModel } from '../models/User.model';
import { CornerstoneService } from '../services/cornerstone/cornerstone.service';
import { RedirectTarget } from '../types';

@Injectable({
  providedIn: 'root',
})
export class RedirectGuard {
  constructor(
    readonly router: Router,
    private userService: UserService,
    private cornerstoneService: CornerstoneService,
  ) {}

  async canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    const id = route.params['id'];
    let redirect = route.queryParams['redirect'];
    const redirectTargets: RedirectTarget = environment.redirect;

    // Evita redirecionamento para outros sites ou possíveis ataques XSS/SQLi
    if (redirect) {
      const cleanRedirect = this.multiDecode(redirect).trim();

      const hasExternal = cleanRedirect.toLowerCase().includes('http');
      const isProtocolRelative = /^\s*\/\//.test(cleanRedirect);
      const hasDangerousScheme = /^\s*(javascript|data|vbscript)\s*:/i.test(cleanRedirect);
      const hasDangerousChars =
        cleanRedirect.includes('<') ||
        cleanRedirect.includes('>') ||
        cleanRedirect.includes('"') ||
        cleanRedirect.includes("'") ||
        cleanRedirect.includes(';') ||
        cleanRedirect.includes('--') ||
        cleanRedirect.includes('/*') ||
        cleanRedirect.includes('*/');

      // Bloqueia URLs externas, protocol-relative, esquemas perigosos e caracteres perigosos
      if (hasExternal || isProtocolRelative || hasDangerousScheme || hasDangerousChars) {
        redirect = null;
      } else {
        // Permite apenas caminhos relativos seguros
        redirect = cleanRedirect.replace(/[^a-zA-Z0-9\-_\/\?&=\.\#\^\+ ]/g, '');
      }
    }

    // Busca o target de redirecionamento pelo token (id)
    const target = Object.values(redirectTargets).find(x => x.hash === id);

    // Se o token não existir na lista, redireciona para login
    if (!target) {
      await this.router.navigate(['/login']);
      return false;
    }

    // Verifica permissão antes de redirecionar
    if (target.role) {
      try {
        const res: UserLoginModel = await lastValueFrom(this.userService.getMe());
        const hasPermission = res.roles.some((x: any) => target.role == x.id || target.roles?.includes(x.id));

        if (!hasPermission) {
          await this.router.navigate(['/panel']);
          return false;
        }
      } catch {
        await this.router.navigate(['/panel']);
        return false;
      }
    }

    // Tenta obter o token criptografado
    try {
      if (id === redirectTargets.cornerstone.hash) {
        const redirectBase = redirect ?? 'LMS/catalog/Welcome.aspx?tab_page_id=-67&tab_id=-1';
        const cornerstoneRedirect = await lastValueFrom(this.cornerstoneService.gotoCornerstoneText(redirectBase));

        if (
          cornerstoneRedirect &&
          typeof cornerstoneRedirect === 'string' &&
          cornerstoneRedirect.trim().startsWith('<')
        ) {
          this.submitHtmlForm(cornerstoneRedirect);
          return false;
        }
      } else {
        const encryptedToken = await lastValueFrom(this.userService.encryptToken());

        if (encryptedToken) {
          window.location.assign(
            `${target.link}?token=${encodeURIComponent(encryptedToken)}${redirect ? '&redirect=' + redirect : ''}`,
          );
          return false;
        }
      }

      await this.router.navigate(['/login']);
    } catch {
      await this.router.navigate(['/login']);
    }

    return false;
  }

  private submitHtmlForm(html: string): void {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      const incomingForm = doc.querySelector('form');
      if (!incomingForm) {
        throw new Error('Nenhum formulário encontrado no HTML retornado.');
      }

      const action = (incomingForm.getAttribute('action') || '').trim();
      const method = (incomingForm.getAttribute('method') || 'GET').toUpperCase();
      if (!action) {
        throw new Error('Ação do formulário (action) não encontrada.');
      }

      // Cria um form oculto na página atual para submeter via POST
      const form = document.createElement('form');
      form.style.display = 'none';
      form.method = method;
      form.action = action;
      form.target = '_self';

      // Copia os campos do formulário original (inputs, selects, textareas)
      const fields = incomingForm.querySelectorAll('input, select, textarea');
      fields.forEach((el: Element) => {
        const name = (el as HTMLInputElement).name;
        // Apenas campos com nome são enviados em formulários
        if (!name) return;

        const type = (el as HTMLInputElement).getAttribute('type') || '';
        // Ignora inputs do tipo submit/button ao recriar
        if (type.toLowerCase() === 'submit' || type.toLowerCase() === 'button') return;

        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;

        if (el instanceof HTMLInputElement) {
          input.value = el.value ?? '';
        } else if (el instanceof HTMLTextAreaElement) {
          input.value = el.value ?? '';
        } else if (el instanceof HTMLSelectElement) {
          input.value = el.value ?? '';
        }

        form.appendChild(input);
      });

      document.body.appendChild(form);
      form.submit();
    } catch (err) {
      // Fallback: substitui o documento atual pelo HTML retornado
      try {
        const newDoc = document.open('text/html', 'replace');
        newDoc.write(html);
        newDoc.close();
      } catch (fallbackErr) {
        console.error('Falha ao submeter HTML de redirecionamento', err, fallbackErr);
      }
    }
  }

  // Normaliza com múltiplos decodes (até 5x) e remove espaços
  private multiDecode(value: string, maxTimes = 5): string {
    if (maxTimes < 1) {
      return '';
    }

    try {
      const decoded = decodeURIComponent(value);

      // Se ainda contém '%', tenta decodificar novamente
      if (decoded.includes('%')) {
        return this.multiDecode(decoded, maxTimes - 1);
      }

      return decoded;
    } catch {
      return '';
    }
  }
}
